import Buzzsumo from "@/assets/svg/Buzzsumo.svg?react";
import { agGridTheme } from "@/lib/ag-grid-theme";
import { cn } from "@/lib/utils";
import { useSegmentLookup } from "@/store/share-of-voice/segment/segment.selectors";

import {
  GaugeIcon,
  SmileyIcon,
  SmileyMehIcon,
  SmileySadIcon,
} from "@phosphor-icons/react";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useRef } from "react";
import MetricsCellRenderer from "./MetricsCellRenderer";
import NoRowsOverlay from "./NoRowsOverlay";
import {
  type ShareVoiceMetricsTableData,
  categorizeSentiment,
  useCalculateColumnTotal,
  useGetShareOfVoiceMetricsTableData,
} from "./useMetricsTable";

const ShareOfVoiceMetricsTable = () => {
  const { data, fetching, error, isRefetching } =
    useGetShareOfVoiceMetricsTableData();
  const segmentLookup = useSegmentLookup() as Map<
    string,
    { color: string; name: string; isVisible: boolean }
  >;
  const gridRef = useRef<AgGridReact>(null);

  const isLoadingTableData = isRefetching || fetching;

  const calculateColumnTotal = useCalculateColumnTotal();

  const skeletonData = isLoadingTableData
    ? Array.from(
        { length: 5 },
        () =>
          ({
            segmentName: "",
            totalArticles: 0,
            netImpact: 0,
            grossImpact: 0,
            netSentiment: 0,
            grossPositiveImpact: 0,
            grossNeutral: 0,
            grossNegativeImpact: 0,
            socialEngagement: 0,
            mentions: 0,
            averageArticleImpact: 0,
            averageImpactStrength: 0,
          }) as ShareVoiceMetricsTableData,
      )
    : [];

  const columnDefs: ColDef<ShareVoiceMetricsTableData>[] = [
    {
      field: "segmentId",
      headerName: "Segments",
      width: 200,
      minWidth: 180,
      resizable: true,
      lockPosition: true,
      pinned: "left",
      cellRenderer: (
        params: ICellRendererParams<ShareVoiceMetricsTableData>,
      ) => {
        const segmentId = params.data?.segmentId;
        const segmentInfo = segmentId ? segmentLookup.get(segmentId) : null;

        return (
          <MetricsCellRenderer
            isLoading={isLoadingTableData}
            params={{
              ...params,
              value: segmentInfo?.name || "",
            }}
            prefixRenderer={
              <span
                className="w-2 h-2 rounded-full inline-block mr-2"
                style={{ backgroundColor: segmentInfo?.color }}
                role="presentation"
                aria-label="Color indicator"
              />
            }
          />
        );
      },
    },
    {
      field: "totalArticles",
      headerName: "Total Articles",
      width: 150,
      minWidth: 130,
      resizable: true,
      cellRenderer: (
        params: ICellRendererParams<ShareVoiceMetricsTableData>,
      ) => (
        <MetricsCellRenderer
          isLoading={isLoadingTableData}
          params={params}
          columnTotal={calculateColumnTotal(data, "totalArticles")}
          numberFormat="commas"
        />
      ),
    },
    {
      field: "netImpact",
      headerName: "Net LLM Impact Score",
      width: 150,
      minWidth: 100,
      resizable: true,
      cellRenderer: (
        params: ICellRendererParams<ShareVoiceMetricsTableData>,
      ) => (
        <MetricsCellRenderer
          isLoading={isLoadingTableData}
          params={params}
          prefixRenderer={<GaugeIcon className={"h-4 w-4 text-violet-800"} />}
          valueClassName={"text-violet-800"}
          numberFormat="commas"
        />
      ),
    },
    {
      field: "averageArticleImpact",
      headerName: "Average Article Impact",
      width: 150,
      minWidth: 130,
      resizable: true,
      cellRenderer: (
        params: ICellRendererParams<ShareVoiceMetricsTableData>,
      ) => (
        <MetricsCellRenderer
          isLoading={isLoadingTableData}
          params={params}
          valueClassName={"text-violet-800"}
          numberFormat={"abbreviate"}
        />
      ),
    },
    {
      field: "averageImpactStrength",
      headerName: "Average Impact Strength",
      width: 150,
      minWidth: 130,
      resizable: true,
      cellRenderer: (
        params: ICellRendererParams<ShareVoiceMetricsTableData>,
      ) => (
        <MetricsCellRenderer
          isLoading={isLoadingTableData}
          params={params}
          valueClassName={"text-violet-800"}
          numberFormat={"abbreviate"}
        />
      ),
    },
    {
      field: "grossImpact",
      headerName: "Gross Impact",
      width: 150,
      minWidth: 130,
      resizable: true,
      cellRenderer: (
        params: ICellRendererParams<ShareVoiceMetricsTableData>,
      ) => (
        <MetricsCellRenderer
          isLoading={isLoadingTableData}
          params={params}
          columnTotal={calculateColumnTotal(data, "grossImpact")}
          prefixRenderer={<GaugeIcon className={"h-4 w-4 text-violet-800"} />}
          valueClassName={"text-violet-800"}
          numberFormat={"commas"}
        />
      ),
    },
    {
      field: "netSentiment",
      headerName: "Net Sentiment",
      width: 150,
      minWidth: 110,
      resizable: true,
      cellRenderer: (
        params: ICellRendererParams<ShareVoiceMetricsTableData>,
      ) => (
        <MetricsCellRenderer
          isLoading={isLoadingTableData}
          params={params}
          prefixRenderer={(() => {
            const netSentiment = params.data?.netSentiment;
            if (netSentiment === null || netSentiment === undefined)
              return undefined;

            const sentimentCategory = categorizeSentiment(netSentiment);
            return (
              <span
                className={cn(
                  "px-2.5 py-1 rounded-md font-medium truncate",
                  sentimentCategory === "Positive" &&
                    "text-green-700 bg-green-50",
                  sentimentCategory === "Negative" && "text-red-700 bg-red-50",
                  sentimentCategory === "Neutral" && "text-gray-600 bg-gray-50",
                )}
              >
                {sentimentCategory}
              </span>
            );
          })()}
        />
      ),
    },
    {
      field: "grossPositiveImpact",
      headerName: "Gross Positive Impact",
      width: 180,
      minWidth: 150,
      resizable: true,
      cellRenderer: (
        params: ICellRendererParams<ShareVoiceMetricsTableData>,
      ) => (
        <MetricsCellRenderer
          isLoading={isLoadingTableData}
          params={params}
          columnTotal={calculateColumnTotal(data, "grossPositiveImpact")}
          prefixRenderer={<SmileyIcon className={"h-4 w-4 text-green-700"} />}
          valueClassName={"text-green-700"}
          numberFormat={"commas"}
        />
      ),
    },
    {
      field: "grossNeutral",
      headerName: "Gross Neutral",
      width: 150,
      minWidth: 130,
      resizable: true,
      cellRenderer: (
        params: ICellRendererParams<ShareVoiceMetricsTableData>,
      ) => (
        <MetricsCellRenderer
          isLoading={isLoadingTableData}
          params={params}
          columnTotal={calculateColumnTotal(data, "grossNeutral")}
          prefixRenderer={<SmileyMehIcon className={"h-4 w-4 text-gray-600"} />}
          numberFormat={"commas"}
        />
      ),
    },
    {
      field: "grossNegativeImpact",
      headerName: "Gross Negative Impact",
      width: 180,
      minWidth: 130,
      resizable: true,
      cellRenderer: (
        params: ICellRendererParams<ShareVoiceMetricsTableData>,
      ) => (
        <MetricsCellRenderer
          isLoading={isLoadingTableData}
          params={params}
          columnTotal={calculateColumnTotal(data, "grossNegativeImpact")}
          prefixRenderer={<SmileySadIcon className={"h-4 w-4 text-red-800"} />}
          valueClassName={"text-red-800"}
          numberFormat={"commas"}
        />
      ),
    },
    {
      field: "socialEngagement",
      headerName: "Social Engagement",
      width: 180,
      minWidth: 150,
      resizable: true,
      cellRenderer: (
        params: ICellRendererParams<ShareVoiceMetricsTableData>,
      ) => (
        <MetricsCellRenderer
          isLoading={isLoadingTableData}
          params={params}
          columnTotal={calculateColumnTotal(data, "socialEngagement")}
          prefixRenderer={<Buzzsumo height={12} />}
          numberFormat={"commas"}
        />
      ),
    },
    {
      field: "mentions",
      headerName: "Total Readership",
      width: 120,
      cellRenderer: (
        params: ICellRendererParams<ShareVoiceMetricsTableData>,
      ) => (
        <MetricsCellRenderer
          isLoading={isLoadingTableData}
          params={params}
          numberFormat={"abbreviate"}
        />
      ),
    },
  ];

  // Filter out rows where segment is not visible
  const filteredData = useMemo(() => {
    if (isLoadingTableData || !data) return skeletonData;

    return data.filter((row) => {
      const segmentInfo = segmentLookup.get(row.segmentId);
      return segmentInfo?.isVisible !== false; // Keep row if segment is visible or not found
    });
  }, [data, segmentLookup, isLoadingTableData, skeletonData]);

  // Force AG Grid to refresh when filtered data changes
  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.refreshCells();
    }
  }, [filteredData]);

  // Show error state
  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-red-500">
          Error loading metrics data: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full h-full"
      style={
        {
          "--ag-grid-min-height": filteredData.length === 0 ? "100px" : "auto",
        } as React.CSSProperties
      }
    >
      <AgGridReact
        ref={gridRef}
        rowData={filteredData}
        columnDefs={columnDefs}
        defaultColDef={{
          sortable: true,
          resizable: true,
        }}
        domLayout="autoHeight"
        theme={agGridTheme}
        loadingOverlayComponent={() => null}
        noRowsOverlayComponent={NoRowsOverlay}
        enableCellTextSelection
        suppressDragLeaveHidesColumns={true}
      />
    </div>
  );
};

export default ShareOfVoiceMetricsTable;
